import type { CalibrationBin } from '../../api'

/**
 * Reliability: what was said, what happened, and the distance between them.
 *
 * Lifted out of `ModelPage` unchanged when that page grew sub-navigation. It is the largest single
 * thing on the Record panel and had no business sharing a file with the model history.
 */

/**
 * Is this bin's miss bigger than chance produces at this sample size?
 *
 * A bin of 11 predictions saying 66.8% and landing 81.8% looks alarming and is 9 of 11 — the
 * binomial standard error there is 14 points, so a 15-point miss is inside one and a half of them.
 * Two standard errors is the usual ~95% line, and this is the quantity that decides whether a row
 * gets a colour at all. Without it the loudest marks on the chart are the ones resting on n=1.
 *
 * Not a substitute for the proper test — Hosmer–Lemeshow over the whole table, which is in
 * docs/UNDER-CONFIDENCE.md and also finds nothing — but it is the right per-row gate.
 */
function beyondChance(bin: CalibrationBin): boolean {
  const predicted = bin.predicted ?? 0
  if (bin.count < 1) return false
  const standardError = Math.sqrt((predicted * (1 - predicted)) / bin.count)
  return Math.abs((bin.actual ?? 0) - predicted) > 2 * standardError
}

/**
 * Reliability as a dumbbell: what was said, what happened, and the distance between them.
 *
 * **What this replaces and why.** It was a bar per bin running from zero to the observed rate. Every
 * observed rate on this site sits between 47% and 100%, so every bar was long and they all looked
 * alike; the quantity carrying the entire message — the gap between the bar's end and the marker —
 * was a handful of pixels inside a bar four hundred wide. The chart drew the part that does not
 * matter at full size and the part that does at nearly none.
 *
 * Three things fix it. The axis starts at the data rather than at zero, so the gap gets the width.
 * The mark IS the gap: a hollow dot at the claim, a filled dot at the outcome, a rule between them.
 * And the dot area scales with the sample, so a bin holding one match cannot draw the same weight as
 * one holding thirty-five.
 *
 * Colour is spent only where it is earned. A row is drawn in the diagnosis pair when its miss
 * exceeds two standard errors and in neutral otherwise — which, on the current record, is every row.
 * That is the honest picture, and it is the opposite of what the old chart showed.
 *
 * Still hand-drawn rather than pulled from a chart library, and now for a stronger reason than
 * before: percentage-positioned divs stay responsive with no width measurement, and the marker has
 * to sit at each bin's own mean prediction rather than at the bin's midpoint — which chart defaults
 * get wrong and quietly turn into a flattering picture.
 */
export function CalibrationChart({ bins }: { bins: CalibrationBin[] }) {
  const filled = bins.filter((b) => b.count > 0)

  if (filled.length === 0) {
    return <p className="text-sm text-ink-faint">Not enough scored predictions yet.</p>
  }

  // Zoom to the data, rounded out to a whole ten so the gridlines read as round numbers.
  const values = filled.flatMap((b) => [b.predicted ?? 0, b.actual ?? 0])
  const lo = Math.max(0, Math.floor((Math.min(...values) * 100 - 5) / 10) * 10)
  const hi = Math.min(100, Math.ceil((Math.max(...values) * 100 + 5) / 10) * 10)
  const at = (p: number) => ((p * 100 - lo) / (hi - lo)) * 100

  const ticks: number[] = []
  for (let tick = lo; tick <= hi; tick += 10) ticks.push(tick)

  const biggest = Math.max(...filled.map((b) => b.count))
  // Area, not diameter, carries the count — a diameter scale exaggerates a large bin fourfold.
  const size = (count: number) => 7 + 11 * Math.sqrt(count / biggest)

  return (
    <div className="space-y-2">
      {filled.map((bin) => {
        const actual = bin.actual ?? 0
        const predicted = bin.predicted ?? 0
        const notable = beyondChance(bin)
        const tone = !notable
          ? 'bg-diag-ok'
          : actual > predicted
            ? 'bg-diag-under-fill'
            : 'bg-diag-over-fill'
        const [left, right] = [Math.min(at(predicted), at(actual)), Math.max(at(predicted), at(actual))]
        const dot = size(bin.count)

        return (
          <div key={bin.from} className="flex items-center gap-2 text-xs sm:gap-3">
            <span className="tabular w-14 shrink-0 text-ink-faint sm:w-16">
              {(bin.from * 100).toFixed(0)}–{(bin.to * 100).toFixed(0)}
            </span>

            <div className="relative h-7 min-w-0 flex-1">
              {ticks.map((tick) => (
                <div
                  key={tick}
                  className="absolute inset-y-0 w-px bg-line"
                  style={{ left: `${at(tick / 100)}%` }}
                />
              ))}

              {/* The gap itself, which is the whole point of the chart. */}
              <div
                className={`absolute top-1/2 h-0.5 -translate-y-1/2 ${tone}`}
                style={{ left: `${left}%`, width: `${Math.max(right - left, 0)}%` }}
              />

              {/* What happened: filled, sized by how much evidence stands behind it. */}
              <div
                className={`absolute top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full ${tone}`}
                style={{ left: `${at(actual)}%`, width: dot, height: dot }}
              />

              {/* What the model said: hollow, at this bin's own mean — not the bin's midpoint. */}
              <div
                className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-ink bg-surface"
                style={{ left: `${at(predicted)}%`, width: dot, height: dot }}
              />
            </div>

            {/* The readout is the width the track is competing with, so it contracts on a phone
                rather than squeezing the chart into a third of the row. */}
            <span className="tabular w-14 shrink-0 whitespace-nowrap text-right text-ink-soft sm:hidden">
              {(predicted * 100).toFixed(0)}→{(actual * 100).toFixed(0)}
            </span>
            <span className="tabular hidden w-32 shrink-0 whitespace-nowrap text-right text-ink-soft sm:inline">
              said {(predicted * 100).toFixed(0)} · was {(actual * 100).toFixed(0)}
            </span>
            <span className="tabular w-10 shrink-0 text-right text-ink-faint sm:w-12">
              n={bin.count}
            </span>
          </div>
        )
      })}

      {/* The axis, aligned to the track by repeating the row's spacer widths. */}
      <div className="flex items-center gap-2 text-xs sm:gap-3">
        <span className="w-14 shrink-0 sm:w-16" />
        <div className="relative h-4 min-w-0 flex-1">
          {ticks.map((tick, index) => (
            <span
              key={tick}
              // Every 20 points on a phone, every 10 from `sm` up. Eight labels across 130px is not
              // a denser axis, it is an unreadable one — they overlapped into a single smear.
              className={`tabular absolute -translate-x-1/2 text-ink-faint ${
                index % 2 === 1 ? 'hidden sm:inline' : ''
              }`}
              style={{ left: `${at(tick / 100)}%` }}
            >
              {tick}%
            </span>
          ))}
        </div>
        <span className="w-14 shrink-0 sm:w-32" />
        <span className="w-10 shrink-0 sm:w-12" />
      </div>

      <p className="pt-2 text-xs text-ink-faint">
        Hollow dot = what the model said. Filled dot = what happened, drawn larger where more matches
        stand behind it. A row is coloured only when the gap is bigger than chance produces at that
        sample size —{' '}
        {/* Read off the data, not asserted. A sentence claiming "none of them are" would go on
            saying so after the day one of them was. */}
        {filled.some(beyondChance)
          ? `${filled.filter(beyondChance).length} of ${filled.length} currently ${
              filled.filter(beyondChance).length === 1 ? 'is' : 'are'
            }.`
          : 'none of them are.'}
      </p>
    </div>
  )
}

